/**
 * Accelerators announcement banner: the one-time "New: apply to Techstars,
 * YC and more" card on the signed-in home page for returning founders. Pure
 * copy, dismissal-key and deep-link logic; the hook
 * (useAcceleratorsAnnouncementBanner) reads Clerk/flags and dispatches it.
 *
 * Dismissal lives in Clerk `unsafeMetadata.dismissedAnnouncements`, not a
 * project's uiState (this banner has no single owning project) and not
 * localStorage (the operator's standing rule: every banner's dismissal
 * persists past the browser, same device or not). `ACCELERATORS_ANNOUNCEMENT_KEY`
 * is this banner's own slot in that map; other keys already living there
 * (`referral_id`, a future announcement's own key) MUST survive a write here,
 * so every caller merges (withAcceleratorsAnnouncementDismissed) rather than
 * replacing `unsafeMetadata` wholesale.
 *
 * A `user.update` write can fail (network, rate limit). The in-mount override
 * alone does not survive a remount (opening a project and coming back to
 * `/`), so a failed write would show the banner again within the SAME
 * session even though the founder already dismissed it once. The session
 * fallback (isAcceleratorsAnnouncementSessionDismissed /
 * markAcceleratorsAnnouncementSessionDismissed) is scoped per Clerk user id
 * and checked alongside the Clerk-metadata read; it is not a replacement for
 * the durable write, only a same-tab backstop until one succeeds.
 */
import { programsViewHref } from "@/constants/acceleratorsRow";
import { SS_ACCELERATORS_ANNOUNCEMENT_DISMISSED_PREFIX } from "@/lib/storage-keys";

/** This banner's slot in `unsafeMetadata.dismissedAnnouncements`. Never reused for a later announcement. */
export const ACCELERATORS_ANNOUNCEMENT_KEY = "accelerators-2026-10";

/** Operator-approved copy (LEV announcement review). Never claims a partnership with any program. */
export const ACCELERATORS_ANNOUNCEMENT_COPY = {
  heading: "New: apply to Techstars, YC and more with Lev.",
  body: "Lev drafts your application in each program's format, scores it, and shows what to fix first.",
  button: "Open Programs",
} as const;

/** Garbage/missing metadata reads as not-dismissed; only an explicit `true` counts. */
export function isAcceleratorsAnnouncementDismissed(unsafeMetadata: unknown): boolean {
  const dismissed = (unsafeMetadata as { dismissedAnnouncements?: unknown } | null | undefined)
    ?.dismissedAnnouncements;
  return (dismissed as Record<string, unknown> | null | undefined)?.[
    ACCELERATORS_ANNOUNCEMENT_KEY
  ] === true;
}

/**
 * The full `unsafeMetadata` object to pass to `user.update`. Clerk replaces
 * `unsafeMetadata` wholesale on write, so every existing top-level key
 * (`referral_id`) and every other announcement's dismissal is spread back in;
 * only this banner's own key changes.
 */
export function withAcceleratorsAnnouncementDismissed(
  unsafeMetadata: Record<string, unknown> | null | undefined,
): Record<string, unknown> {
  const existing = unsafeMetadata ?? {};
  const dismissedAnnouncements =
    (existing.dismissedAnnouncements as Record<string, unknown> | null | undefined) ?? {};
  return {
    ...existing,
    dismissedAnnouncements: { ...dismissedAnnouncements, [ACCELERATORS_ANNOUNCEMENT_KEY]: true },
  };
}

export interface AcceleratorsAnnouncementProject {
  id: string;
  updatedAt: Date | string;
  ownerId: string;
  archivedAt: Date | string | null;
  deletedAt: Date | string | null;
}

/**
 * "Returning founder" projects: owned by the viewer, not archived, not
 * deleted. Archiving and deleting both bump `updatedAt` (Prisma `@updatedAt`),
 * so an unfiltered list can read a project the founder just archived or
 * deleted as their most recent work; a project shared with the viewer (owned
 * by someone else) never counts either — a collaborator with no project of
 * their own is not a returning founder for this banner. `viewerUserId`
 * missing (no resolved auth context) means nothing can be verified as
 * owned, so it returns none rather than guessing.
 */
export function ownedActiveProjects(
  projects: ReadonlyArray<AcceleratorsAnnouncementProject>,
  viewerUserId: string | undefined,
): AcceleratorsAnnouncementProject[] {
  if (!viewerUserId) return [];
  return projects.filter(
    (p) => p.ownerId === viewerUserId && !p.archivedAt && !p.deletedAt,
  );
}

/** The most recently updated project, or null when the founder has none yet. */
export function mostRecentlyUpdatedProjectId(
  projects: ReadonlyArray<{ id: string; updatedAt: Date | string }>,
): string | null {
  if (projects.length === 0) return null;
  return projects.reduce((latest, p) =>
    new Date(p.updatedAt).getTime() > new Date(latest.updatedAt).getTime() ? p : latest,
  ).id;
}

/** The canvas URL "Open Programs" opens: the row's Programs view, no program landed on. */
export function acceleratorsAnnouncementHref(projectId: string): string {
  return programsViewHref(projectId);
}

function isBrowser(): boolean {
  return typeof window !== "undefined";
}

function sessionDismissKey(clerkUserId: string): string {
  return `${SS_ACCELERATORS_ANNOUNCEMENT_DISMISSED_PREFIX}/${clerkUserId}`;
}

/** The session-fallback marker (see the file header); false with no user id, no window, or no marker written yet. */
export function isAcceleratorsAnnouncementSessionDismissed(
  clerkUserId: string | undefined,
): boolean {
  if (!isBrowser() || !clerkUserId) return false;
  try {
    return window.sessionStorage.getItem(sessionDismissKey(clerkUserId)) === "1";
  } catch {
    // benign: sessionStorage unavailable (private mode, blocked storage) — no marker reads as not dismissed, the safe default.
    return false;
  }
}

/** Writes the session-fallback marker. Best-effort: a failed write only means this tab may show the banner again. */
export function markAcceleratorsAnnouncementSessionDismissed(
  clerkUserId: string | undefined,
): void {
  if (!isBrowser() || !clerkUserId) return;
  try {
    window.sessionStorage.setItem(sessionDismissKey(clerkUserId), "1");
  } catch {
    // benign: sessionStorage unavailable (private mode, blocked storage) — the in-mount override still hides the banner for the rest of this mount.
  }
}
